import { useState } from "react";
import { Phone, Copy, Check } from "lucide-react";
import { BUSINESS } from "@/lib/site-data";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

type Props = {
  className?: string;
  label?: string;
  variant?: "primary" | "link";
};

function isMobileDevice() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia("(pointer: coarse)").matches ||
    window.matchMedia("(max-width: 768px)").matches
  );
}

export function CallNowButton({ className, label = "Call Now", variant = "primary" }: Props) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (!isMobileDevice()) {
      e.preventDefault();
      setOpen(true);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(BUSINESS.phone);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* noop */
    }
  };

  const baseClass =
    variant === "primary"
      ? "inline-flex items-center gap-2 rounded-full bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition hover:bg-primary/90"
      : "inline-flex items-center gap-1.5 font-medium text-primary hover:underline";

  return (
    <>
      <a
        href={BUSINESS.phoneHref}
        onClick={handleClick}
        aria-label={`${label} ${BUSINESS.phone}`}
        className={className ?? baseClass}
      >
        <Phone className="h-4 w-4" aria-hidden="true" />
        {label}
      </a>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Call {BUSINESS.shortName}</DialogTitle>
            <DialogDescription>{BUSINESS.hours}</DialogDescription>
          </DialogHeader>
          <div className="mt-2 flex flex-col items-center gap-4">
            <a
              href={BUSINESS.phoneHref}
              className="font-display text-3xl tracking-tight text-foreground hover:text-primary"
            >
              {BUSINESS.phone}
            </a>
            <div className="flex w-full flex-col gap-2 sm:flex-row">
              <a
                href={BUSINESS.phoneHref}
                className="inline-flex flex-1 items-center justify-center gap-2 rounded-full bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground transition hover:bg-primary/90"
              >
                <Phone className="h-4 w-4" aria-hidden="true" />
                Call now
              </a>
              <button
                type="button"
                onClick={handleCopy}
                className="inline-flex flex-1 items-center justify-center gap-2 rounded-full border border-border px-4 py-2.5 text-sm font-medium text-foreground transition hover:bg-muted"
              >
                {copied ? (
                  <>
                    <Check className="h-4 w-4" aria-hidden="true" />
                    Copied
                  </>
                ) : (
                  <>
                    <Copy className="h-4 w-4" aria-hidden="true" />
                    Copy number
                  </>
                )}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}